import { forwardRef, Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { InappRealtimeStreamControl } from './inapp-realtime-stream.interface';
import { shouldHoldInappSseConnection } from './inapp-sse-visibility-rules';

/** In-app row kind: informational vs action required. */
export type InappItemType = 'notification' | 'task';

/**
 * In-app bell rows (persisted in MySQL, Redis queue on backend).
 *
 * Real-time badge updates via SSE (Redis Pub/Sub); REST polling as fallback only.
 */
export interface InappNotificationRow {
  id: number;
  user_id: number;
  category: string;
  code: string;
  item_type: InappItemType;
  title: string;
  message: string | null;
  link: string | null;
  payload?: Record<string, unknown> | string | null;
  is_read: string | number;
  is_done?: string | number;
  created_at: string;
}

interface BadgeUpdateEvent {
  type?: string;
  item_type?: InappItemType;
  notification_count?: number | string;
  task_count?: number | string;
  title?: string;
  message?: string;
  link?: string;
}

@Injectable({
  providedIn: 'root',
})
export class InappNotificationsService implements OnDestroy, InappRealtimeStreamControl {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(forwardRef(() => AuthService));
  private readonly ngZone = inject(NgZone);

  private readonly baseUrl = `${environment.serverUrl}inapp_notifications/`;
  private readonly streamUrl = `${environment.serverUrl}Notification_stream/subscribe`;

  private readonly notificationCountSubject = new BehaviorSubject<number>(0);
  private readonly taskCountSubject = new BehaviorSubject<number>(0);

  private streamStarted = false;
  private sseConnected = false;
  private streamAbort: AbortController | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private skipNextReconnect = false;
  private reconnectAttempt = 0;
  private pageHideHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  private readonly reconnectBaseMs = 3000;
  private readonly reconnectMaxMs = 60000;

  badgeCount$(itemType: InappItemType): Observable<number> {
    return itemType === 'task'
      ? this.taskCountSubject.asObservable()
      : this.notificationCountSubject.asObservable();
  }

  list(
    limit = 25,
    offset = 0,
    itemType: InappItemType = 'notification'
  ): Observable<{ status: string; data: InappNotificationRow[] }> {
    return this.http.get<{ status: string; data: InappNotificationRow[] }>(
      `${this.baseUrl}list_items?limit=${limit}&offset=${offset}&item_type=${itemType}`
    );
  }

  unreadCount(
    itemType: InappItemType = 'notification'
  ): Observable<{ status: string; count: number | string }> {
    return this.http.get<{ status: string; count: number | string }>(
      `${this.baseUrl}unread_count?item_type=${itemType}`
    );
  }

  markAllRead(itemType: InappItemType = 'notification'): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.baseUrl}mark_all_read?item_type=${itemType}`,
      {}
    );
  }

  markRead(id: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.baseUrl}mark_read`, { id });
  }

  markDone(id: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.baseUrl}mark_done`, { id });
  }

  markAllDone(): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.baseUrl}mark_all_done?item_type=task`,
      {}
    );
  }

  /** Idempotent: one shared SSE connection for both bells. */
  startRealtimeStream(): void {
    if (!this.auth.isLogged() || !this.auth.getToken()) {
      return;
    }

    this.refreshAllCountsFromApi();

    if (this.streamStarted) {
      return;
    }

    this.streamStarted = true;
    this.bindLifecycleHandlers();
    this.syncStreamWithVisibility(this.auth.getToken()!);
  }

  stopRealtimeStream(): void {
    this.streamStarted = false;
    this.sseConnected = false;
    this.reconnectAttempt = 0;
    this.unbindLifecycleHandlers();
    this.closeStreamOnly(true);
    this.notificationCountSubject.next(0);
    this.taskCountSubject.next(0);
  }

  refreshAllCountsFromApi(): void {
    if (!this.auth.isLogged()) {
      return;
    }
    this.refreshBadgeCount('notification');
    this.refreshBadgeCount('task');
  }

  refreshBadgeCount(itemType: InappItemType): void {
    if (!this.auth.isLogged()) {
      return;
    }

    this.unreadCount(itemType).subscribe({
      next: (res) => {
        if (res?.status !== 'success') {
          return;
        }
        const count = this.coerceCount(res.count);
        if (count !== null) {
          this.setBadgeCount(itemType, count);
        }
      },
      error: () => {},
    });
  }

  setBadgeCount(itemType: InappItemType, count: number): void {
    const safe = Math.max(0, Math.floor(count));
    this.ngZone.run(() => {
      if (itemType === 'task') {
        this.taskCountSubject.next(safe);
      } else {
        this.notificationCountSubject.next(safe);
      }
    });
  }

  adjustBadgeCount(itemType: InappItemType, delta: number): void {
    const subject =
      itemType === 'task' ? this.taskCountSubject : this.notificationCountSubject;
    this.setBadgeCount(itemType, subject.value + delta);
  }

  ngOnDestroy(): void {
    this.stopRealtimeStream();
  }

  private openStream(token: string): void {
    this.closeStreamOnly(true);
    this.streamAbort = new AbortController();

    void this.consumeSseStream(token, this.streamAbort.signal).finally(() => {
      if (this.skipNextReconnect || !this.streamStarted) {
        this.skipNextReconnect = false;
        return;
      }
      this.sseConnected = false;
      // Hidden tabs must not reconnect — they would re-occupy a scarce HTTP slot.
      if (
        typeof document !== 'undefined' &&
        !shouldHoldInappSseConnection(this.streamStarted, document.hidden)
      ) {
        return;
      }
      this.scheduleReconnect(token);
    });
  }

  private async consumeSseStream(token: string, signal: AbortSignal): Promise<void> {
    const response = await fetch(this.streamUrl, {
      method: 'GET',
      headers: {
        Authorization: token,
        Accept: 'text/event-stream',
      },
      signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`SSE connection failed (${response.status})`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      buffer = this.processSseBuffer(buffer);
    }
  }

  private processSseBuffer(buffer: string): string {
    const parts = buffer.split('\n\n');
    const remainder = parts.pop() ?? '';

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed || trimmed.startsWith(':')) {
        continue;
      }

      const dataLines = part
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => this.normalizeSseDataLine(line));

      if (dataLines.length === 0) {
        continue;
      }

      this.handleSsePayload(dataLines.join('\n'));
    }

    return remainder;
  }

  private normalizeSseDataLine(line: string): string {
    let payload = line.trim();
    while (payload.toLowerCase().startsWith('data:')) {
      payload = payload.slice(5).trimStart();
    }
    return payload;
  }

  private scheduleReconnect(token: string): void {
    if (!this.streamStarted) {
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    const delayMs = Math.min(
      this.reconnectBaseMs * 2 ** this.reconnectAttempt,
      this.reconnectMaxMs
    );
    this.reconnectAttempt += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.streamStarted) {
        return;
      }
      const freshToken = this.auth.getToken() ?? token;
      this.openStream(freshToken);
    }, delayMs);
  }

  private handleSsePayload(raw: string): void {
    const normalized = this.normalizeSseDataLine(raw.startsWith('data:') ? raw : `data:${raw}`);
    if (!normalized) {
      return;
    }

    let data: BadgeUpdateEvent;
    try {
      data = JSON.parse(normalized) as BadgeUpdateEvent;
    } catch {
      return;
    }

    this.ngZone.run(() => {
      if (data.type === 'connected') {
        this.sseConnected = true;
        this.reconnectAttempt = 0;
        // Single event-driven catch-up on (re)connect. No periodic polling.
        this.refreshAllCountsFromApi();
        return;
      }

      if (data.type === 'error') {
        this.sseConnected = false;
        this.closeStreamOnly(true);
        return;
      }

      if (data.type !== 'badge_update') {
        return;
      }

      const notificationCount = this.coerceCount(data.notification_count);
      const taskCount = this.coerceCount(data.task_count);

      // Payload counts are authoritative — no extra HTTP call.
      if (notificationCount !== null) {
        this.setBadgeCount('notification', notificationCount);
      }
      if (taskCount !== null) {
        this.setBadgeCount('task', taskCount);
      }
    });
  }

  private coerceCount(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, value);
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return Math.max(0, parsed);
      }
    }
    return null;
  }

  private closeStreamOnly(intentional = false): void {
    if (intentional) {
      this.skipNextReconnect = true;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.streamAbort) {
      this.streamAbort.abort();
      this.streamAbort = null;
    }
  }

  private bindLifecycleHandlers(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (!this.pageHideHandler) {
      this.pageHideHandler = () => {
        this.closeStreamOnly(true);
      };
      window.addEventListener('pagehide', this.pageHideHandler);
    }
    if (!this.visibilityHandler) {
      this.visibilityHandler = () => {
        const token = this.auth.getToken();
        if (!token || !this.streamStarted) {
          return;
        }
        this.syncStreamWithVisibility(token);
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  private unbindLifecycleHandlers(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  /**
   * Keep SSE only on the focused tab so opening many lender sheets
   * ("Open in new tab") does not exhaust the browser's per-host connection pool.
   */
  private syncStreamWithVisibility(token: string): void {
    const hidden = typeof document !== 'undefined' ? document.hidden : false;
    if (!shouldHoldInappSseConnection(this.streamStarted, hidden)) {
      this.closeStreamOnly(true);
      this.sseConnected = false;
      return;
    }
    if (!this.streamAbort) {
      this.openStream(token);
    }
  }
}
